import React from 'react';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { useAtom } from 'jotai';
import { useLocation } from '@tanstack/react-router';
import { panelSizesAtomFamily } from './atom';

type TwoColumnProps = {

  header?: React.ReactNode;
  /**
   * 左側のパネルに表示するコンテンツ
   */
  leftPane: React.ReactNode;
  /**
   * 右側のパネルに表示するコンテンツ
   */
  rightPane: React.ReactNode;
  /**
   * 左パネルの最小幅(%)
   * @default 20
   */
  minLeftWidth?: number;
  /**
   * 右パネルの最小幅(%)
   * @default 30
   */
  minRightWidth?: number;
  /**
   * 初期分割幅を設定するためのID（ルートパスが自動設定されます）
   */
  layoutId?: string;
  /**
   * パネルのスプリッターの位置を固定するかどうか
   * @default false
   */
  fixed?: boolean;
};

/**
 * リサイズ可能な2カラムレイアウトコンポーネント
 *
 * 画面間でリサイズ状態を保持するために、ルートパスごとにatomにサイズを保存します。
 */
const TwoColumn: React.FC<TwoColumnProps> = ({
  header,
  leftPane,
  rightPane,
  minLeftWidth = 20,
  minRightWidth = 30,
  layoutId,
  fixed = false,
}) => {
  const location = useLocation();
  // layoutIdが指定されていなければルートパスを使用
  const storeKey = layoutId || location.pathname;
  const [panelSizes, setPanelSizes] = useAtom(panelSizesAtomFamily(storeKey));

  // パネルのリサイズイベントハンドラー
  interface SplitterResizeEndEvent {
    sizes?: number[];
  }

  const handleResizeEnd = (e: SplitterResizeEndEvent): void => {
    if (fixed) return;

    // パネルのサイズを計算（Splitterが返すsizes配列はパーセント値）
    const newSizes = e.sizes ?? [panelSizes.leftPanel, panelSizes.rightPanel];
    setPanelSizes({
      leftPanel: newSizes[0],
      rightPanel: newSizes[1],
    });
  };

  return (
    <div className="p-0 m-0 gap-0">
      {header}
      <Splitter
        className="w-full h-full tw-min-h-[calc(100vh-100px)]"
        layout="horizontal"
        stateKey={`two-column-${storeKey}`}
        stateStorage="local"
        onResizeEnd={handleResizeEnd}
        style={{ height: '100%' }}
      >
        <SplitterPanel
          size={panelSizes.leftPanel}
          minSize={minLeftWidth}
          className="p-2"
          style={{ minWidth: 0 }}
        >
          {leftPane}
        </SplitterPanel>
        <SplitterPanel
          size={panelSizes.rightPanel}
          minSize={minRightWidth}
          className="p-2"
          style={{ minWidth: 0 }}
        >
          {rightPane}
        </SplitterPanel>
      </Splitter>
    </div>
  );
};

export default TwoColumn;
